/**
 * Mode Comparison Card (MA-UI-107)
 * 
 * Side-by-side comparison of Capture Only vs Enforce modes.
 * Proves whether stricter compliance mode creates better downstream quality.
 */

import React from 'react';
import { Scale, Info, TrendingUp, Award, Minus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

// =============================================================================
// COMPARISON ROW COMPONENT
// =============================================================================

function ComparisonRow({ label, captureValue, enforceValue, formatFn, lowerIsBetter = false }) {
  const coVal = captureValue ?? null;
  const enVal = enforceValue ?? null;
  
  // Determine which is better
  let coBetter = false;
  let enBetter = false;
  
  if (coVal !== null && enVal !== null) {
    if (lowerIsBetter) {
      coBetter = coVal < enVal;
      enBetter = enVal < coVal;
    } else {
      coBetter = coVal > enVal;
      enBetter = enVal > coVal;
    }
  }
  
  const formatValue = (val) => {
    if (val === null || val === undefined) return '—';
    return formatFn ? formatFn(val) : val;
  };

  return (
    <div className="grid grid-cols-3 gap-4 py-2.5 border-b border-white/5 last:border-0">
      <div className="text-xs text-slate-400">{label}</div>
      <div className={`text-sm font-medium text-center ${coBetter ? 'text-emerald-400' : 'text-slate-300'}`}>
        <div className="flex items-center justify-center gap-1">
          {formatValue(coVal)}
          {coBetter && <TrendingUp className="h-3 w-3 text-emerald-400" />}
        </div>
      </div>
      <div className={`text-sm font-medium text-center ${enBetter ? 'text-emerald-400' : 'text-slate-300'}`}>
        <div className="flex items-center justify-center gap-1">
          {formatValue(enVal)}
          {enBetter && <TrendingUp className="h-3 w-3 text-emerald-400" />}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// MODE COMPARISON CARD
// =============================================================================

export function ModeComparisonCard({ captureOnly, enforce, summary, isLoading }) {
  // Loading state
  if (isLoading) {
    return (
      <Card className="glass border-white/10">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <div className="animate-pulse h-4 bg-slate-700 rounded w-32"></div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-3">
            <div className="grid grid-cols-3 gap-4">
              <div className="h-4 bg-slate-700 rounded"></div>
              <div className="h-4 bg-slate-700 rounded"></div>
              <div className="h-4 bg-slate-700 rounded"></div>
            </div>
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="grid grid-cols-3 gap-4">
                <div className="h-3 bg-slate-700 rounded"></div>
                <div className="h-3 bg-slate-700 rounded"></div>
                <div className="h-3 bg-slate-700 rounded"></div>
              </div>
            ))}
            <div className="h-12 bg-slate-700 rounded mt-4"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Empty state
  if (!captureOnly && !enforce) {
    return (
      <Card className="glass border-white/10">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Scale className="h-4 w-4 text-slate-400" />
            <CardTitle className="text-sm font-medium text-slate-300">Mode Comparison</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-400">
            No funnel leads with TrustedForm modes in this period. Enable TrustedForm on your funnels to see comparison data.
          </p>
        </CardContent>
      </Card>
    );
  }

  const co = captureOnly || {};
  const en = enforce || {};
  const winner = summary?.winner;
  const insight = summary?.insight;

  // Format helpers
  const formatPercent = (val) => `${(val * 100).toFixed(0)}%`;
  const formatMoney = (val) => val !== null ? `$${val.toFixed(2)}` : '—';

  return (
    <Card 
      className="glass border-white/10"
      data-testid="mode-comparison-card"
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-indigo-500/10">
              <Scale className="h-4 w-4 text-indigo-400" />
            </div>
            <div>
              <CardTitle className="text-sm font-medium text-slate-200">Mode Comparison</CardTitle>
              <CardDescription className="text-xs text-slate-400">
                Compare lead usability and business performance across compliance modes.
              </CardDescription>
            </div>
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Info className="h-3.5 w-3.5 text-slate-500" />
              </TooltipTrigger>
              <TooltipContent side="left" className="max-w-xs">
                <p className="text-xs">
                  This comparison helps prove whether stricter compliance modes (Enforce) create 
                  better downstream operating quality compared to less strict modes (Capture Only).
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </CardHeader>

      <CardContent>
        {/* Column Headers */}
        <div className="grid grid-cols-3 gap-4 pb-2 border-b border-white/10 mb-2">
          <div className="text-xs text-slate-500 uppercase tracking-wider">Metric</div>
          <div className="text-center">
            <div className="text-xs font-medium text-blue-300">Capture Only</div>
            <div className="text-[10px] text-slate-500">{co.leads?.toLocaleString() || 0} leads</div>
          </div>
          <div className="text-center">
            <div className="text-xs font-medium text-indigo-300">Enforce</div>
            <div className="text-[10px] text-slate-500">{en.leads?.toLocaleString() || 0} leads</div>
          </div>
        </div>

        {/* Comparison Rows */}
        <div className="space-y-0">
          <ComparisonRow 
            label="Deployable Rate"
            captureValue={co.deployable_rate}
            enforceValue={en.deployable_rate}
            formatFn={formatPercent}
          />
          <ComparisonRow 
            label="Missing TrustedForm"
            captureValue={co.missing_trustedform_rate}
            enforceValue={en.missing_trustedform_rate}
            formatFn={formatPercent}
            lowerIsBetter={true}
          />
          <ComparisonRow 
            label="Quarantine Rate"
            captureValue={co.quarantine_rate}
            enforceValue={en.quarantine_rate}
            formatFn={formatPercent}
            lowerIsBetter={true}
          />
          <ComparisonRow 
            label="Profit per Lead"
            captureValue={co.profit_per_lead}
            enforceValue={en.profit_per_lead}
            formatFn={formatMoney}
          />
        </div>

        {/* Insight Strip */}
        <div className={`mt-4 p-3 rounded-lg ${
          winner === 'enforce' 
            ? 'bg-indigo-500/10 border border-indigo-500/20' 
            : winner === 'capture_only'
            ? 'bg-blue-500/10 border border-blue-500/20'
            : 'bg-slate-800/50 border border-slate-700/50'
        }`}>
          <div className="flex items-start gap-2">
            {winner && (
              <Award className={`h-4 w-4 mt-0.5 flex-shrink-0 ${
                winner === 'enforce' ? 'text-indigo-400' : 'text-blue-400'
              }`} />
            )}
            {!winner && (
              <Minus className="h-4 w-4 mt-0.5 flex-shrink-0 text-slate-500" />
            )}
            <p className="text-xs text-slate-300 leading-relaxed">
              {insight || 'Not enough data for confident comparison between compliance modes.'}
            </p>
          </div>
          
          {/* Winner badge */}
          {winner && (
            <div className="mt-2">
              <Badge 
                variant="outline" 
                className={`text-xs ${
                  winner === 'enforce' 
                    ? 'bg-indigo-500/10 text-indigo-300 border-indigo-500/30' 
                    : 'bg-blue-500/10 text-blue-300 border-blue-500/30'
                }`}
              >
                {winner === 'enforce' ? 'Enforce Mode Leading' : 'Capture Only Leading'}
              </Badge>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default ModeComparisonCard;
